import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, TextField } from "@mui/material";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { useSearch } from "@/utils";
import { useLazyGeSuggestionQuery } from "@/pages/Private/redux/liPeople/liPeople.api";
import { classNames } from "@/utils/classNames";
import { Icon } from "@/components";

export interface LinkedinParams {
	title: string;
	urn: string;
}

interface LinkedinTagAutocompleteProps {
	value?: LinkedinParams | null;
	type: string;
	handleValueChange: (value: LinkedinParams | null) => void;
	isDisabled?: boolean;
}

export const LinkedinTagAutocompleteSingle: FunctionComponent<LinkedinTagAutocompleteProps> = ({
	value,
	type,
	handleValueChange,
	isDisabled,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liPeople.${key}`);
	const { query, setSearchValue, searchValue } = useSearch();
	const [inputValue, setInputValue] = useState<string>();
	const [options, setOptions] = useState<LinkedinParams[]>([]);
	const [defaults, setDefaults] = useState<LinkedinParams>();
	const [fetchTags, { isLoading }] = useLazyGeSuggestionQuery();

	useEffect(() => {
		setInputValue(query);
	}, [query, type]);

	useEffect(() => {
		if (value) {
			setDefaults(value);
		}
	}, [value]);

	useEffect(() => {
		if (inputValue) {
			fetchTags({ type, keyword: inputValue }).then((results) => {
				const optionValues = results?.data?.suggestions;

				if (optionValues) {
					setOptions([...optionValues]);
				}
			});
		}
	}, [inputValue, fetchTags, type]);

	return (value && defaults) || !value ? (
		<div className={classNames(isDisabled ? "cursor-not-allowed opacity-[0.5]" : "cursor-pointer")}>
			<Autocomplete
				blurOnSelect={true}
				disabled={isDisabled}
				getOptionLabel={(option) => option.title}
				id={type}
				loading={isLoading}
				loadingText={"Searching..."}
				noOptionsText={searchValue.length > 0 ? "Typing..." : "Start typing to search"}
				options={options}
				popupIcon={<Icon className="text-gray-500 h-[24px] w-[15px]" icon={faChevronDown} />}
				renderInput={(params) => <TextField {...params} placeholder={ts(`${type}.label`)} />}
				size="medium"
				sx={{
					width: "100%",
					height: "48px",
					backgroundColor: "white",
					"& .MuiFilledInput-root": {
						paddingTop: "4px!important",
						paddingBottom: "4px!important",
						marginBottom: "0px!important",
					},
					"& .MuiFormControl-root": {
						height: "48px !important",
					},
					"& .MuiInputBase-root": {
						height: "48px !important",
						borderRadius: "0.75rem",
					},
				}}
				value={value}
				onChange={(event, value, reason) => {
					if (reason === "clear") {
						handleValueChange(null);
						setSearchValue("");
						setInputValue("");

						return;
					}

					if (value) {
						handleValueChange(value);
					} else {
						handleValueChange(null);
						setInputValue("");
						setSearchValue("");
					}
				}}
				onInputChange={(event, newInputValue) => {
					setSearchValue(newInputValue);
				}}
			/>
		</div>
	) : (
		<></>
	);
};
